/**
 * <ElapsedText> — le temps écoulé d'une carte du live (« 42s », puis « 3 min »), qui se recompte seul (atom).
 *
 * Un minuteur posé sur le prochain changement du texte (à la seconde pendant la première minute, puis à la minute) :
 * seul ce texte se redessine. L'écran ne se réveille plus pour faire avancer un chiffre — à la seconde, il
 * redessinait toute la salle du masteur, vidéos comprises. Sans `since`, le texte donné est fixe (une heure).
 */
import React, { useEffect, useState } from "react"

import { formatElapsed, nextElapsedChange } from "../../../ports/primary/ui/formatters/tutorFeedFormatter"
import { Text } from "../typography/Text"

export type ElapsedTextProps = {
    /** Le texte tel que l'écran l'a calculé : rendu seulement quand `since` est absent. */
    text: string
    /** L'instant (ms) d'où il compte. Absent : `text` est fixe. */
    since?: number
    testID?: string
}

export function ElapsedText(props: ElapsedTextProps) {
    const { text, since, testID } = props
    const [now, setNow] = useState(() => Date.now())
    useEffect(() => {
        if (since == null) return undefined
        let timer: ReturnType<typeof setTimeout> | undefined
        const arm = () => {
            const current = Date.now()
            timer = setTimeout(
                () => {
                    setNow(Date.now())
                    arm()
                },
                Math.max(0, nextElapsedChange(since, current) - current)
            )
        }
        // Le temps a pu passer depuis le premier rendu : on ne relit que si le texte a changé.
        const current = Date.now()
        setNow((before) => (formatElapsed(since, before) === formatElapsed(since, current) ? before : current))
        arm()
        return () => clearTimeout(timer)
    }, [since])
    return (
        <Text size="text-xs" color="text-muted" testID={testID}>
            {since == null ? text : formatElapsed(since, now)}
        </Text>
    )
}
